/**
 * Phase 17 — Mock Email Outbox admin page.
 *
 * Visibility: only mounted when `/api/admin/email-outbox/visibility`
 * returns `visible:true`. The sibling AdminLayout already filters this
 * out of the nav when disabled.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import {
  Mail,
  Search,
  RefreshCw,
  Forward,
  Archive as ArchiveIcon,
  Trash2,
  Copy,
  X,
  Filter,
  Inbox,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "../../lib/api";
import AdminPageHeader from "./AdminPageHeader";

const STATUS_BADGE = {
  pending: { label: "Pending", color: "var(--warning-fg)", bg: "var(--warning-bg)", border: "var(--warning-border)" },
  forwarded: { label: "Forwarded", color: "#3F7E2D", bg: "rgba(94,171,69,0.10)", border: "rgba(94,171,69,0.30)" },
  archived: { label: "Archived", color: "var(--page-fg-muted)", bg: "var(--surface-2)", border: "var(--border-subtle)" },
};

function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const ms = Date.now() - d.getTime();
    const mins = Math.round(ms / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}

export default function EmailOutbox() {
  const { readOnly } = useOutletContext() || {};
  const [visibility, setVisibility] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [templateFilter, setTemplateFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [activeRow, setActiveRow] = useState(null);
  const [previewBody, setPreviewBody] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const refreshTimerRef = useRef(null);

  // -------- visibility gate --------
  useEffect(() => {
    api
      .get("/admin/email-outbox/visibility")
      .then((r) => setVisibility(r.data))
      .catch(() => setVisibility({ visible: false, reason: "denied" }));
  }, []);

  // -------- data load --------
  const load = async () => {
    if (!visibility?.visible) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("status", statusFilter);
      if (templateFilter && templateFilter !== "all") params.set("template_id", templateFilter);
      if (search.trim()) params.set("q", search.trim());
      params.set("limit", "100");
      const { data } = await api.get(`/admin/email-outbox?${params.toString()}`);
      setRows(data.items || []);
      setTotal(data.total || 0);
      setPendingCount(data.pending_count || 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    setSelectedIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, templateFilter, visibility?.visible]);

  // search debounce
  useEffect(() => {
    if (!visibility?.visible) return;
    const id = setTimeout(load, 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // 30s auto-refresh
  useEffect(() => {
    if (!visibility?.visible) return;
    if (autoRefresh) {
      refreshTimerRef.current = setInterval(load, 30_000);
      return () => clearInterval(refreshTimerRef.current);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, statusFilter, templateFilter, search, visibility?.visible]);

  if (!visibility) return null;
  if (!visibility.visible) return <Navigate to="/admin" replace />;

  const templates = Array.from(
    new Set(rows.map((r) => r.template_id).filter(Boolean)),
  );

  // -------- mutations --------
  const handleMarkForwarded = async (id) => {
    try {
      await api.post(`/admin/email-outbox/${id}/mark-forwarded`);
      toast.success("Marked as forwarded");
      load();
      if (activeRow?.id === id) setActiveRow({ ...activeRow, status: "forwarded" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleArchive = async (id) => {
    try {
      await api.post(`/admin/email-outbox/${id}/archive`);
      toast.success("Archived");
      load();
      if (activeRow?.id === id) setActiveRow({ ...activeRow, status: "archived" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/email-outbox/${id}`);
      toast.success("Deleted");
      setConfirmDelete(null);
      if (activeRow?.id === id) setActiveRow(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleBulkMarkForwarded = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      await api.post("/admin/email-outbox/mark-forwarded-bulk", { ids });
      toast.success(`Marked ${ids.length} as forwarded`);
      setSelectedIds(new Set());
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const openDrawer = async (row) => {
    setActiveRow(row);
    setPreviewBody(null);
    setPreviewLoading(true);
    try {
      const { data } = await api.get(`/admin/email-outbox/${row.id}/preview`);
      setPreviewBody(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const copy = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Couldn't copy — please select manually.");
    }
  };

  const toggleAll = () => {
    if (selectedIds.size === rows.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(rows.map((r) => r.id)));
  };

  return (
    <div data-testid="admin-email-outbox-page" className="space-y-5">
      <AdminPageHeader
        eyebrow="Admin · Email Outbox"
        title="Email Outbox"
        description={
          <>
            Mock email queue. <span className="font-mono text-xs">{visibility.reason}</span>
            <span className="ml-2 text-xs">
              When real email is configured, this panel auto-hides.
            </span>
          </>
        }
        rightSlot={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              data-testid="email-outbox-refresh"
              disabled={loading}
            >
              <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <label className="flex items-center gap-1 text-xs text-[color:var(--page-fg-secondary)]">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                data-testid="email-outbox-autorefresh"
                className="h-3.5 w-3.5"
              />
              Auto-refresh 30s
            </label>
          </div>
        }
      />

      {/* Quick stats */}
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Pending" value={pendingCount} icon={Inbox} accent="var(--warning-fg)" />
        <StatCard label="In view" value={rows.length} icon={Mail} accent="var(--page-fg-secondary)" />
        <StatCard label="Total (this filter)" value={total} icon={Filter} accent="var(--page-fg-muted)" />
      </div>

      {/* Tabs + search */}
      <div className="brand-card p-4">
        <Tabs value={statusFilter} onValueChange={setStatusFilter} data-testid="email-outbox-tabs">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <TabsList>
              <TabsTrigger value="pending" data-testid="tab-pending">
                Pending
                {pendingCount > 0 && (
                  <span
                    className="ml-1.5 inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-white tabular-nums"
                    style={{ backgroundColor: "var(--warning-fg)" }}
                  >
                    {pendingCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="forwarded" data-testid="tab-forwarded">Forwarded</TabsTrigger>
              <TabsTrigger value="archived" data-testid="tab-archived">Archived</TabsTrigger>
              <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[color:var(--page-fg-muted)]" />
                <Input
                  className="h-9 w-64 pl-7 text-sm"
                  placeholder="Search subject / recipient / template"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="email-outbox-search"
                />
              </div>
              <Select value={templateFilter} onValueChange={setTemplateFilter}>
                <SelectTrigger className="h-9 w-44 text-sm" data-testid="email-outbox-template-filter">
                  <SelectValue placeholder="All templates" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All templates</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <TabsContent value={statusFilter} className="mt-4">
            {selectedIds.size > 0 && statusFilter === "pending" && (
              <div
                className="mb-3 flex items-center justify-between rounded-lg px-3 py-2"
                style={{ backgroundColor: "var(--surface-2)", border: "1px solid var(--border-subtle)" }}
                data-testid="email-outbox-bulk-bar"
              >
                <span className="text-sm text-[color:var(--page-fg-secondary)]">
                  {selectedIds.size} selected
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleBulkMarkForwarded}
                    data-testid="email-outbox-bulk-mark-forwarded"
                    className="goodwin-primary-btn text-white"
                  >
                    <Forward className="mr-1 h-3.5 w-3.5" />
                    Mark all as forwarded
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedIds(new Set())}
                    data-testid="email-outbox-bulk-clear"
                  >
                    Clear selection
                  </Button>
                </div>
              </div>
            )}

            {rows.length === 0 ? (
              <div
                className="rounded-lg border border-dashed py-12 text-center text-sm"
                style={{ borderColor: "var(--border-subtle)", color: "var(--page-fg-muted)" }}
                data-testid="email-outbox-empty"
              >
                <Mail className="mx-auto mb-2 h-6 w-6" strokeWidth={1.5} />
                Nothing here. Triggering a forgot-password or bulk invite will queue emails.
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-[color:var(--border-subtle)]">
                <table className="w-full text-sm">
                  <thead
                    className="text-left text-xs uppercase tracking-wider"
                    style={{
                      backgroundColor: "var(--surface-2)",
                      color: "var(--page-fg-muted)",
                    }}
                  >
                    <tr>
                      {statusFilter === "pending" && (
                        <th className="w-10 px-3 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.size > 0 && selectedIds.size === rows.length}
                            onChange={toggleAll}
                            data-testid="email-outbox-select-all"
                          />
                        </th>
                      )}
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">To</th>
                      <th className="px-4 py-3">Subject</th>
                      <th className="px-4 py-3">Template</th>
                      <th className="px-4 py-3">Sent</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[color:var(--border-subtle)]">
                    {rows.map((row) => {
                      const badge = STATUS_BADGE[row.status] || STATUS_BADGE.pending;
                      const checked = selectedIds.has(row.id);
                      return (
                        <tr
                          key={row.id}
                          data-testid={`email-outbox-row-${row.id}`}
                          className="cursor-pointer transition-colors hover:bg-[color:var(--surface-2)]"
                        >
                          {statusFilter === "pending" && (
                            <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  const next = new Set(selectedIds);
                                  if (checked) next.delete(row.id);
                                  else next.add(row.id);
                                  setSelectedIds(next);
                                }}
                                data-testid={`email-outbox-select-${row.id}`}
                              />
                            </td>
                          )}
                          <td className="px-4 py-3" onClick={() => openDrawer(row)}>
                            <span
                              className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.10em]"
                              style={{
                                backgroundColor: badge.bg,
                                color: badge.color,
                                border: `1px solid ${badge.border}`,
                              }}
                            >
                              {badge.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-[color:var(--page-fg)]" onClick={() => openDrawer(row)}>
                            {row.to}
                          </td>
                          <td className="px-4 py-3 text-[color:var(--page-fg)]" onClick={() => openDrawer(row)}>
                            <div className="line-clamp-1 max-w-[420px]">{row.subject}</div>
                          </td>
                          <td className="px-4 py-3 text-xs text-[color:var(--page-fg-secondary)]" onClick={() => openDrawer(row)}>
                            {row.template_id}
                          </td>
                          <td className="px-4 py-3 text-xs text-[color:var(--page-fg-secondary)]" onClick={() => openDrawer(row)}>
                            {fmtRelative(row.created_at)}
                          </td>
                          <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-end gap-1.5">
                              {row.status === "pending" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleMarkForwarded(row.id)}
                                  data-testid={`email-outbox-mark-forwarded-${row.id}`}
                                  title="Mark as forwarded after sending manually"
                                >
                                  <Forward className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              {row.status !== "archived" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleArchive(row.id)}
                                  data-testid={`email-outbox-archive-${row.id}`}
                                  title="Archive"
                                >
                                  <ArchiveIcon className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setConfirmDelete(row)}
                                data-testid={`email-outbox-delete-${row.id}`}
                                title="Delete"
                              >
                                <Trash2 className="h-3.5 w-3.5" style={{ color: "var(--danger-fg)" }} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Side drawer */}
      <Drawer
        open={!!activeRow}
        onClose={() => setActiveRow(null)}
        row={activeRow}
        body={previewBody}
        loading={previewLoading}
        onMarkForwarded={() => activeRow && handleMarkForwarded(activeRow.id)}
        onArchive={() => activeRow && handleArchive(activeRow.id)}
        onDelete={() => activeRow && setConfirmDelete(activeRow)}
        onCopy={copy}
      />

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent data-testid="email-outbox-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete this email?</DialogTitle>
            <DialogDescription>
              <span className="font-mono">{confirmDelete?.to}</span> · {confirmDelete?.subject}
              <br />
              This row will be permanently removed from the outbox. An audit row
              is written. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => handleDelete(confirmDelete.id)}
              data-testid="email-outbox-delete-confirm"
            >
              <Trash2 className="mr-1 h-4 w-4" />
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="brand-card flex items-center gap-3 px-4 py-3">
      <div
        className="flex h-9 w-9 items-center justify-center rounded-lg"
        style={{ backgroundColor: "var(--surface-2)", color: accent }}
      >
        <Icon className="h-4 w-4" strokeWidth={1.6} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-[0.10em] text-[color:var(--page-fg-muted)]">
          {label}
        </p>
        <p className="text-2xl font-semibold tabular-nums text-[color:var(--page-fg)]">{value}</p>
      </div>
    </div>
  );
}

function Drawer({ open, onClose, row, body, loading, onMarkForwarded, onArchive, onDelete, onCopy }) {
  const [tab, setTab] = useState("html");
  useEffect(() => {
    setTab("html");
  }, [row?.id]);
  if (!open) return null;
  const badge = STATUS_BADGE[row?.status] || STATUS_BADGE.pending;
  return (
    <div
      data-testid="email-outbox-drawer"
      className="fixed inset-0 z-[60] flex"
      style={{ backgroundColor: "rgba(15,23,42,0.40)" }}
      onClick={onClose}
    >
      <div className="flex-1" />
      <div
        className="flex h-full w-full max-w-[640px] flex-col overflow-hidden bg-[color:var(--page-bg)] shadow-2xl"
        style={{ borderLeft: "1px solid var(--border-subtle)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between border-b border-[color:var(--border-subtle)] p-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.10em]"
                style={{
                  backgroundColor: badge.bg,
                  color: badge.color,
                  border: `1px solid ${badge.border}`,
                }}
              >
                {badge.label}
              </span>
              <span className="text-xs text-[color:var(--page-fg-muted)]">{row?.template_id}</span>
            </div>
            <h2 className="mt-2 line-clamp-2 break-words font-serif text-xl font-semibold text-[color:var(--page-fg)]">
              {row?.subject}
            </h2>
            <p className="mt-1 font-mono text-xs text-[color:var(--page-fg-secondary)]">
              To: {row?.to}{row?.to_name ? ` <${row.to_name}>` : ""}
            </p>
            <p className="font-mono text-xs text-[color:var(--page-fg-muted)]">
              Created: {row?.created_at}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={onClose}
            data-testid="email-outbox-drawer-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div className="flex flex-wrap gap-2 border-b border-[color:var(--border-subtle)] p-3">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onCopy(row?.subject, "Subject")}
            data-testid="drawer-copy-subject"
          >
            <Copy className="mr-1 h-3.5 w-3.5" />
            Copy subject
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onCopy(body?.body_text, "Plain text body")}
            data-testid="drawer-copy-text"
          >
            <Copy className="mr-1 h-3.5 w-3.5" />
            Copy plain text
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onCopy(body?.body_html, "HTML body")}
            data-testid="drawer-copy-html"
          >
            <Copy className="mr-1 h-3.5 w-3.5" />
            Copy HTML
          </Button>
          <span className="flex-1" />
          {row?.status === "pending" && (
            <Button size="sm" onClick={onMarkForwarded} data-testid="drawer-mark-forwarded" className="goodwin-primary-btn text-white">
              <Forward className="mr-1 h-3.5 w-3.5" />
              Mark forwarded
            </Button>
          )}
          {row?.status !== "archived" && (
            <Button size="sm" variant="outline" onClick={onArchive} data-testid="drawer-archive">
              <ArchiveIcon className="mr-1 h-3.5 w-3.5" />
              Archive
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onDelete} data-testid="drawer-delete">
            <Trash2 className="mr-1 h-3.5 w-3.5" style={{ color: "var(--danger-fg)" }} />
            Delete
          </Button>
        </div>

        <div className="flex-1 overflow-auto">
          <Tabs value={tab} onValueChange={setTab} className="h-full">
            <TabsList className="m-3">
              <TabsTrigger value="html" data-testid="drawer-tab-html">HTML preview</TabsTrigger>
              <TabsTrigger value="text" data-testid="drawer-tab-text">Plain text</TabsTrigger>
            </TabsList>
            <TabsContent value="html" className="px-3 pb-6">
              {loading ? (
                <div className="h-40 animate-pulse rounded-md bg-[color:var(--surface-2)]" />
              ) : (
                <iframe
                  title="Email HTML preview"
                  data-testid="drawer-html-iframe"
                  srcDoc={body?.body_html || "<p>(empty)</p>"}
                  className="h-[600px] w-full rounded-md border border-[color:var(--border-subtle)] bg-white"
                  sandbox=""
                />
              )}
            </TabsContent>
            <TabsContent value="text" className="px-3 pb-6">
              <pre
                data-testid="drawer-text-pre"
                className="whitespace-pre-wrap break-words rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 font-mono text-xs"
                style={{ color: "var(--page-fg)" }}
              >
                {loading ? "Loading..." : body?.body_text || "(empty)"}
              </pre>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
